import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { transcodeOnClient as transcodeMediaClient } from '@joc/media-transcoder/client';
import { getFileUrl, resolveNormalizedFile, uploadAttachment } from 'core/media/uploadPreparedMedia';
import { FileCategory, getFileCategory, getProcessingTooltip, isWebpImage } from './useFileUpload';

export type BatchItemStatus = 'idle' | 'client-transcoding' | 'uploading' | 'server-processing' | 'success' | 'error';

export interface BatchUploadItem {
	id: string;
	file: File;
	fileCategory: FileCategory;
	status: BatchItemStatus;
	progress: number;
	fileName: string | null;
	fileUrl: string | null;
	error: string | null;
	previewUrl: string | null;
}

export interface UseBatchFileUploadOptions {
	defaultConcurrency?: number;
	successHoldMs?: number;
}

export interface UseBatchFileUploadResult {
	items: BatchUploadItem[];
	stagedFiles: File[];
	isSubmitting: boolean;
	isSuccess: boolean;
	isError: boolean;
	totalProgress: number;
	uploadedCount: number;
	totalCount: number;
	uploadedFileNames: string[];
	addFiles: (newFiles: File[] | FileList) => void;
	removeFile: (id: string) => void;
	clearFiles: () => void;
	uploadBatch: (filesOverride?: File[], concurrency?: number) => Promise<string[]>;
	abort: () => void;
	reset: () => void;
}

const createBatchItem = (file: File): BatchUploadItem => {
	const category = getFileCategory(file);
	const id = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}-${file.name}`;
	const previewUrl = category === 'image' ? URL.createObjectURL(file) : null;

	return {
		id,
		file,
		fileCategory: category,
		status: 'idle',
		progress: 0,
		fileName: null,
		fileUrl: null,
		error: null,
		previewUrl,
	};
};

export const useBatchFileUpload = (options?: UseBatchFileUploadOptions): UseBatchFileUploadResult => {
	const defaultConcurrency = options?.defaultConcurrency ?? 2;
	const successHoldMs = options?.successHoldMs ?? 1800;
	const [items, setItems] = useState<BatchUploadItem[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [isError, setIsError] = useState(false);

	const activeCancelSourcesRef = useRef<Map<string, CancelTokenSource>>(new Map());
	const abortedRef = useRef(false);
	const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const resolveHoldRef = useRef<(() => void) | null>(null);
	const itemsRef = useRef<BatchUploadItem[]>([]);
	itemsRef.current = items;

	const addFiles = useCallback((newFiles: File[] | FileList) => {
		const fileArray = Array.from(newFiles);
		if (!fileArray.length) return;

		setItems(prev => {
			const created = fileArray.map(createBatchItem);
			return [...prev, ...created];
		});
		setIsSuccess(false);
		setIsError(false);
	}, []);

	const removeFile = useCallback((id: string) => {
		setItems(prev => {
			const target = prev.find(item => item.id === id);
			if (target?.previewUrl) {
				URL.revokeObjectURL(target.previewUrl);
			}
			return prev.filter(item => item.id !== id);
		});
	}, []);

	const clearFiles = useCallback(() => {
		itemsRef.current.forEach(item => {
			if (item.previewUrl) {
				URL.revokeObjectURL(item.previewUrl);
			}
		});
		setItems([]);
		setIsSuccess(false);
		setIsError(false);
	}, []);

	const abort = useCallback(() => {
		abortedRef.current = true;
		if (holdTimeoutRef.current) {
			clearTimeout(holdTimeoutRef.current);
			holdTimeoutRef.current = null;
		}
		if (resolveHoldRef.current) {
			resolveHoldRef.current();
			resolveHoldRef.current = null;
		}
		activeCancelSourcesRef.current.forEach(source => {
			source.cancel('Batch upload aborted by user');
		});
		activeCancelSourcesRef.current.clear();
		setIsSubmitting(false);
	}, []);

	const reset = useCallback(() => {
		abort();
		clearFiles();
		setIsSubmitting(false);
		setIsSuccess(false);
		setIsError(false);
	}, [abort, clearFiles]);

	const updateItem = useCallback((id: string, patch: Partial<BatchUploadItem>) => {
		setItems(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
	}, []);

	const processFile = useCallback(
		async (item: BatchUploadItem): Promise<string | null> => {
			if (abortedRef.current) return null;

			const normalizedOriginalFile = resolveNormalizedFile(item.file);
			let fileToUpload = normalizedOriginalFile;

			// 1. Selective Client-Side WebP Transcoding
			// ONLY images attempt client-side transcoding to WebP.
			// If already WebP, SKIP client transcoding.
			// If video, document, or other format, COMPLETELY SKIP client transcoding.
			if (item.fileCategory === 'image' && !isWebpImage(normalizedOriginalFile)) {
				updateItem(item.id, { status: 'client-transcoding', progress: 0 });

				try {
					const transcodeResult = await transcodeMediaClient(normalizedOriginalFile);
					if (abortedRef.current) return null;
					if (transcodeResult?.file) {
						fileToUpload = resolveNormalizedFile(transcodeResult.file);
					}
				} catch (transcodeError) {
					if (abortedRef.current) return null;
					console.warn(
						`[useBatchFileUpload] Client WebP transcoding failed for "${item.file.name}", falling back to original:`,
						transcodeError
					);
					fileToUpload = normalizedOriginalFile;
				}
			}

			if (abortedRef.current) return null;

			// 2. Dispatch upload to POST /files/upload-attachment
			updateItem(item.id, { status: 'uploading', progress: 0 });

			const cancelSource = axios.CancelToken.source();
			activeCancelSourcesRef.current.set(item.id, cancelSource);

			try {
				const uploadedFileName = await uploadAttachment(fileToUpload, {
					cancelToken: cancelSource.token,
					onProgress: percent => {
						if (abortedRef.current) return;
						updateItem(item.id, {
							status: percent < 100 ? 'uploading' : 'server-processing',
							progress: percent,
						});
					},
					onProcessing: () => {
						if (abortedRef.current) return;
						updateItem(item.id, {
							status: 'server-processing',
							progress: 100,
						});
					},
				});

				if (abortedRef.current) return null;

				const publicUrl = getFileUrl(uploadedFileName);

				updateItem(item.id, {
					status: 'success',
					progress: 100,
					fileName: uploadedFileName,
					fileUrl: publicUrl,
					error: null,
				});

				return uploadedFileName;
			} catch (err) {
				if (abortedRef.current || axios.isCancel(err)) {
					return null;
				}

				const errorMessage = err instanceof Error ? err.message : String(err);
				updateItem(item.id, {
					status: 'error',
					error: errorMessage,
				});
				return null;
			} finally {
				activeCancelSourcesRef.current.delete(item.id);
			}
		},
		[updateItem]
	);

	const uploadBatch = useCallback(
		async (filesOverride?: File[], concurrency = defaultConcurrency): Promise<string[]> => {
			abortedRef.current = false;
			setIsSubmitting(true);
			setIsSuccess(false);
			setIsError(false);

			let targetItems = itemsRef.current;

			if (filesOverride && filesOverride.length > 0) {
				targetItems.forEach(item => {
					if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
				});
				targetItems = filesOverride.map(createBatchItem);
				setItems(targetItems);
			} else {
				// Reset previously failed items to idle
				setItems(prev =>
					prev.map(item =>
						item.status === 'error' ? { ...item, status: 'idle', progress: 0, error: null } : item
					)
				);
			}

			if (!targetItems.length) {
				setIsSubmitting(false);
				return [];
			}

			// Filter items to upload (skip items already uploaded successfully)
			const itemsToUpload = targetItems.filter(item => item.status !== 'success');
			const resultsMap: Map<string, string> = new Map();

			// Collect existing successful files
			targetItems.forEach(item => {
				if (item.status === 'success' && item.fileName) {
					resultsMap.set(item.id, item.fileName);
				}
			});

			// Bounded Concurrency Worker Pool
			let queueIndex = 0;
			const poolWorker = async (): Promise<void> => {
				while (queueIndex < itemsToUpload.length) {
					if (abortedRef.current) break;
					const currentItem = itemsToUpload[queueIndex++];
					// eslint-disable-next-line no-await-in-loop
					const uploadedName = await processFile(currentItem);
					if (uploadedName) {
						resultsMap.set(currentItem.id, uploadedName);
					}
				}
			};

			const workerCount = Math.max(1, Math.min(concurrency, itemsToUpload.length));
			const workers = Array.from({ length: workerCount }, () => poolWorker());

			await Promise.all(workers);

			if (abortedRef.current) {
				setIsSubmitting(false);
				return [];
			}

			const allSuccess = targetItems.every(item => resultsMap.has(item.id));
			const hasErrors = targetItems.some(item => !resultsMap.has(item.id));

			if (allSuccess && targetItems.length > 0 && successHoldMs > 0) {
				await new Promise<void>(resolve => {
					resolveHoldRef.current = resolve;
					holdTimeoutRef.current = setTimeout(() => {
						holdTimeoutRef.current = null;
						resolveHoldRef.current = null;
						resolve();
					}, successHoldMs);
				});
			}

			setIsSubmitting(false);

			if (abortedRef.current) {
				return [];
			}

			setIsSuccess(allSuccess && targetItems.length > 0);
			setIsError(hasErrors);

			const orderedFileNames = targetItems
				.map(item => resultsMap.get(item.id))
				.filter((name): name is string => typeof name === 'string' && name.length > 0);

			return orderedFileNames;
		},
		[defaultConcurrency, processFile, successHoldMs]
	);

	const stagedFiles = useMemo(() => items.map(item => item.file), [items]);
	const uploadedFileNames = useMemo(
		() =>
			items
				.filter(item => item.status === 'success' && Boolean(item.fileName))
				.map(item => item.fileName as string),
		[items]
	);

	const uploadedCount = uploadedFileNames.length;
	const totalCount = items.length;

	const totalProgress = useMemo(
		() =>
			totalCount === 0
				? 0
				: Math.round(
						items.reduce((sum, item) => sum + (item.status === 'success' ? 100 : item.progress), 0) /
							totalCount
				  ),
		[items, totalCount]
	);

	useEffect(() => {
		return () => {
			abort();
			if (holdTimeoutRef.current) {
				clearTimeout(holdTimeoutRef.current);
				holdTimeoutRef.current = null;
			}
			if (resolveHoldRef.current) {
				resolveHoldRef.current();
				resolveHoldRef.current = null;
			}
			itemsRef.current.forEach(item => {
				if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
			});
		};
	}, [abort]);

	return {
		items,
		stagedFiles,
		isSubmitting,
		isSuccess,
		isError,
		totalProgress,
		uploadedCount,
		totalCount,
		uploadedFileNames,
		addFiles,
		removeFile,
		clearFiles,
		uploadBatch,
		abort,
		reset,
	};
};

export { getProcessingTooltip };
